import React, { useState } from "react"

import { Form, Formik } from "formik"
import * as Yup from "yup"

import { gql, useClient, useMutation } from "hooks/urql"

import Checkbox from "components/Forms/Checkbox"
import {
  Field,
  FormFailures,
  SubmitButton,
  TextAreaField,
  TimeZoneField,
  handleFailure,
} from "components/Forms/Formik/hookComponents"
import GradeField from "components/Forms/Formik/hookComponents/RemoteGradeField"

const emailOrUsernameRequired = Yup.string().test(
  "email-or-username",
  "Email or username is required",
  function (value) {
    const { email, username } = this.parent
    return !!(email && email.trim()) || !!(username && username.trim())
  }
)

const ValidationSchema = Yup.object().shape({
  firstName: Yup.string().required("Please specify a first name"),
  lastName: Yup.string().required("Please specify a last name"),
  email: emailOrUsernameRequired,
  username: emailOrUsernameRequired,
  gradeId: Yup.string().required("Please select a grade"),
  emailTimeZone: Yup.string().required("Please select a time zone"),
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .required("Password is required"),
  passwordConfirmation: Yup.string()
    .required("Please confirm your password")
    .equals([Yup.ref("password")], "Passwords do not match"),
})

const NewStudent = () => {
  const formState = {
    firstName: "",
    lastName: "",
    email: "",
    username: "",
    phoneNumber: "",
    gradeId: "",
    schoolName: "",
    emailTimeZone: "",
    password: "",
    passwordConfirmation: "",
    notes: "",
  }
  const [registerWithUsername, setRegisterWithUsername] = useState(false)
  const client = useClient()
  const [, runMutation] = useMutation(createMutation, client)

  const handleSubmit = (values, actions) => {
    const unusedLoginKey = registerWithUsername ? "email" : "username"
    values[unusedLoginKey] = ""

    runMutation({ input: values })
      .then(
        response => {
          if (response.error) {
            handleFailure(actions, [response.error])
            return
          }

          const { failures } = response.data.studentCreate
          if (failures) {
            handleFailure(actions, failures)
          } else {
            window.location.href = "/"
          }
        },
        e => handleFailure(actions, [e])
      )
      .catch(e => handleFailure(actions, [e]))
  }

  return (
    <Formik
      initialValues={formState}
      validationSchema={ValidationSchema}
      onSubmit={handleSubmit}
      validateOnBlur={false}
      validateOnChange={false}
    >
      <Form>
        <Field name="firstName" label="First Name" autoFocus />
        <Field name="lastName" label="Last Name" />
        {registerWithUsername ? (
          <Field name="username" label="Student Username" />
        ) : (
          <Field name="email" label="Student Email" />
        )}
        <Checkbox
          name="registerWithUsername"
          onChange={e => {
            if (e.currentTarget.checked) {
              setRegisterWithUsername(true)
            } else {
              setRegisterWithUsername(false)
            }
          }}
        >
          Register with username instead
        </Checkbox>
        <Field name="phoneNumber" label="Cell Phone Number" />
        <GradeField />
        <Field name="schoolName" label="School Name" />
        <TimeZoneField name="emailTimeZone" />
        <Field
          name="password"
          type="password"
          label="Password"
          autoComplete="new-password"
        />
        <Field
          name="passwordConfirmation"
          type="password"
          label="Confirm Password"
        />
        <TextAreaField name="notes" label="Notes" />

        <FormFailures />
        <SubmitButton />
      </Form>
    </Formik>
  )
}

const createMutation = gql`
  mutation ($input: StudentInputObject!) {
    studentCreate(input: $input) {
      failures {
        message
      }
    }
  }
`

export default NewStudent
